import { useLookbookNavigationSettingsContext } from '@app/components/lookbook/LookbookSettingsPage/contexts';
import { Button } from '@app/components/various/Button';
import { Edit, Link as LinkIcon, Remove } from '@app/components/various/Icons';
import { Link } from '@app/components/various/Link';
import { Modal } from '@app/components/various/Modal';
import { Id, Lookbook } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { EditLinkForm } from '../Forms/EditLinkForm';
import { ListItem } from '../ListItem';
import { ListItemHeader } from '../ListItem/ListItemHeader';

interface Props {
  onRemoveMenuItem: (id: Id) => void;
  item: Lookbook.LinkNavigationItem;
}

export const LinkNavigationItem = ({ onRemoveMenuItem, item }: Props) => {
  const { t } = useTranslation(['menu', 'common', 'settings']);
  const { addedMenuItems, setNavigationItems } = useLookbookNavigationSettingsContext();
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);

  const handleRemoveMenuItem = React.useCallback(() => {
    onRemoveMenuItem(item.id);
  }, [onRemoveMenuItem, item.id]);

  const handleEdit = (updatedItem: Lookbook.LinkNavigationItem) => {
    setNavigationItems(
      addedMenuItems.map(menuItem => {
        if (menuItem.id === updatedItem.id) {
          return updatedItem;
        }

        if (menuItem.children?.some(child => child.id === updatedItem.id)) {
          return {
            ...menuItem,
            children: menuItem.children.map(child => (child.id === updatedItem.id ? updatedItem : child)),
          };
        }

        return menuItem;
      }),
    );
    setIsEditModalOpen(false);
  };

  return (
    <>
      <ListItem className="group/link-item">
        <ListItemHeader title={item.label} icon={LinkIcon}>
          <Button
            variant="text"
            color="secondary"
            icon={Edit}
            className="opacity-0 group-focus-within/link-item:opacity-100 group-hover/link-item:opacity-100 touch:opacity-100"
            onClick={() => setIsEditModalOpen(true)}
          >
            {t('common:edit')}
          </Button>
          <Button
            aria-label={t('common:remove_item', { item: item.label })}
            icon={Remove}
            variant="text"
            color="danger"
            className="opacity-0 group-focus-within/link-item:opacity-100 group-hover/link-item:opacity-100 touch:opacity-100"
            onClick={handleRemoveMenuItem}
          />
        </ListItemHeader>
        <Link to={item.url} target="_blank" size="medium">
          {item.url}
        </Link>
      </ListItem>
      <Modal isOpen={isEditModalOpen} title={item.label} size="medium" onClose={() => setIsEditModalOpen(false)}>
        <EditLinkForm item={item} onCancel={() => setIsEditModalOpen(false)} onEdit={handleEdit} />
      </Modal>
    </>
  );
};
